import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { leaveApi } from "../../services/leaveApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { KpiStrip } from "../../components/KpiStrip";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { useLocalization } from "../../hooks/useLocalization";

export default function LeaveDashboardPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canViewLeave } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!canViewLeave) return;
    setLoading(true);
    leaveApi.dashboardSummary().then((r) => setSummary(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [canViewLeave]); // eslint-disable-line react-hooks/exhaustive-deps
  useRouteRefresh(load);

  if (!canViewLeave) return <p className="text-sm text-gray-500" data-testid="leave-access-denied">{t("mgmtLeave.accessDenied")}</p>;

  const balances = summary?.employees_with_balances || [];
  const kpiItems = summary ? [
    { label: t("mgmtLeave.kpiEmployeesTracked"), value: balances.length, testId: "kpi-employees-tracked" },
    { label: t("mgmtLeave.kpiPendingRequests"), value: summary.pending_requests, testId: "kpi-pending-requests", tone: summary.pending_requests > 0 ? "warning" : "success", onClick: () => navigate("/management/leave/review") },
    { label: t("mgmtLeave.kpiMandatoryUrgent"), value: summary.mandatory_use_urgent_count, testId: "kpi-mandatory-urgent", tone: summary.mandatory_use_urgent_count > 0 ? "danger" : "success", onClick: () => navigate("/management/leave/mandatory-use") },
    { label: t("mgmtLeave.kpiExpiringSoon"), value: summary.leave_expiring_soon, testId: "kpi-expiring-soon", tone: summary.leave_expiring_soon > 0 ? "warning" : "default" },
    { label: t("mgmtLeave.kpiUpcomingLeave"), value: summary.upcoming_approved_leave, testId: "kpi-upcoming-leave" },
  ] : [];

  return (
    <div className="space-y-5" data-testid="leave-dashboard-page">
      <PageHelpBanner pageKey="leave-dashboard" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtLeave.dashboardHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtLeave.dashboardTitle")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtLeave.dashboardSubtitle")}</p>
      </div>

      {loading && !summary ? <Skeleton className="h-24 w-full rounded-xl" data-testid="leave-kpi-skeleton" /> : summary && <KpiStrip items={kpiItems} testId="leave-kpi-strip" />}

      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" data-testid="leave-nav-review" onClick={() => navigate("/management/leave/review")}>
          {t("mgmtLeave.kpiPendingRequests")} {summary?.pending_requests > 0 && <span className="ml-1 text-red-600 font-bold">({summary.pending_requests})</span>}
        </Button>
        <Button variant="outline" data-testid="leave-nav-policies" onClick={() => navigate("/management/leave/policies")}>{t("mgmtLeave.navPolicies")}</Button>
        <Button variant="outline" data-testid="leave-nav-mandatory-use" onClick={() => navigate("/management/leave/mandatory-use")}>{t("mgmtLeave.navMandatoryUse")}</Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtLeave.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeave.colCurrentBalance")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {loading && Array.from({ length: 4 }).map((_, i) => (
              <tr key={i} className="border-t border-gray-100"><td className="px-4 py-3" colSpan={3}><Skeleton className="h-4 w-full" /></td></tr>
            ))}
            {!loading && balances.map((b) => (
              <tr key={b.employee_id} className="border-t border-gray-100" data-testid={`leave-employee-row-${b.employee_id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{b.employee_name}</td>
                <td className="px-4 py-2 text-gray-800 font-semibold">{b.current_balance} {t("mgmtLeave.daysUnit")}</td>
                <td className="px-4 py-2"><button data-testid={`leave-view-employee-btn-${b.employee_id}`} onClick={() => navigate(`/management/leave/employees/${b.employee_id}`)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtLeave.open")}</button></td>
              </tr>
            ))}
            {!loading && balances.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-500" data-testid="no-leave-employees-row">{t("mgmtLeave.noEmployees")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

